import { useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { initialAvailabilityRange } from '@features/JourneyPickerV2/availabilityRange';
import { useRoomSettings } from '@features/roomSettings';
import { findNextAvailableStay, type NextAvailableStay } from './findNextAvailableStay';
import { useRoomNextAvailableCalendar } from './queries';

const DATE_FORMAT = 'YYYY-MM-DD';
const MONTHS_AHEAD = 2;

type Params = {
  roomCode: string;
  fromDate?: Moment;
  nights: number;
  adults: number;
  children: number;
  isUnavailable: boolean;
  journeyPicker?: { isLoading: boolean };
};

/**
 * The next bookable stay for one room, or null.
 *
 * Only runs when the room is actually unavailable, so the extra request lands on a page that has
 * nothing else to show. Gated on the site's provider and calendar-pricing configuration, which the
 * API resolves into `nextAvailableDatesEnabled`, and on a feature flag for a deploy-free kill
 * switch. A null result means "say nothing" — never "there is no availability".
 *
 * When the API sets `nextAvailableFromPickerRange` and the page has a journey picker, the request
 * asks for the picker's first range and guests and waits for the picker's request, because the
 * provider prices each night with its own call and can answer from what the picker fetched. It
 * waits only until the search has an answer or has failed: switching the query off and on again
 * while the picker loads other months would ask again once the answer is stale, and after every
 * month after a failure.
 *
 * Callers must exclude the basket-constrained case: a room whose units are all already in the
 * basket is bookable on the selected dates, so suggesting other dates would be wrong. Calendar
 * availability is per-night and boolean, so a suggested stay is best-effort on room quantity;
 * selecting it re-runs the real search, which is what decides.
 */
export function useNextAvailableStay({
  roomCode,
  fromDate,
  nights,
  adults,
  children,
  isUnavailable,
  journeyPicker,
}: Params): { nextStay: NextAvailableStay | null; isLoading: boolean } {
  const isFlagOn = useFeatureIsOn('feature.rooms.next-available-dates');
  const { data: roomSettings } = useRoomSettings();

  const isSupported = roomSettings?.data?.nextAvailableDatesEnabled === true;
  const usesPickerRange =
    roomSettings?.data?.nextAvailableFromPickerRange === true && journeyPicker !== undefined;

  const checkIn = fromDate ? moment(fromDate) : moment();
  const checkInDate = checkIn.format(DATE_FORMAT);
  const range = usesPickerRange
    ? initialAvailabilityRange(checkInDate)
    : { start: checkIn, end: checkIn.clone().add(MONTHS_AHEAD, 'month').endOf('month') };
  const startDate = range.start.format(DATE_FORMAT);
  const endDate = range.end.format(DATE_FORMAT);
  const searchKey = [startDate, endDate, adults, children, roomCode].join('|');
  const [answeredSearchKey, setAnsweredSearchKey] = useState<string | null>(null);
  const isWaitingForPicker =
    usesPickerRange && journeyPicker?.isLoading === true && answeredSearchKey !== searchKey;

  const isWanted = isFlagOn && isSupported && isUnavailable && nights > 0 && !!roomCode;

  const { data, isError, isFetching } = useRoomNextAvailableCalendar(
    { startDate, endDate, adults, children, roomCode },
    isWanted && !isWaitingForPicker
  );

  useEffect(() => {
    if (data || isError) {
      setAnsweredSearchKey(searchKey);
    }
  }, [data, isError, searchKey]);

  const nextStay = useMemo(() => {
    if (!isWanted || !data) {
      return null;
    }

    return findNextAvailableStay(data, checkInDate, nights);
  }, [isWanted, data, checkInDate, nights]);

  return {
    nextStay,
    isLoading: isWanted && (isFetching || (isWaitingForPicker && !data)),
  };
}
